import {
  createStore,
  applyMiddleware,
  Dispatch as BaseDispatch,
  Middleware,
} from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';
import { InitialData } from './utils/globals';
import { Actions } from './actions/types';
import reducer, { IState } from './reducers';
import {
  pageNavComplete,
  pageNavError,
  ErrorStatus,
  initialState as pageInitialState,
} from './reducers/page';
import { Actions as PageActions } from './actions/page';
import rootSaga from './sagas';
import { addBreadCrumb } from './bugTracking';

export let store: IStore;

const breadcrumbLogger: Middleware = () => {
  return (next) => (action) => {
    addBreadCrumb({
      message: `createAction:${action.type}`,
      data: action,
    });
    return next(action);
  };
};

const sagaMiddleware = createSagaMiddleware<Actions>();

function setupInitialState(
  initialPageData?: InitialData,
  path?: string
): Partial<IState> {
  if (!initialPageData || !path) {
    return {};
  }

  if (initialPageData.error) {
    let errorPayload:
      | ReturnType<typeof PageActions.pageNavError>['payload']
      | undefined;

    if (initialPageData.article?.article) {
      errorPayload = {
        pageType: 'article',
        articleId: initialPageData.article.article.id,
        path,
        errorStatus: (initialPageData.error.extensions?.code ||
          'HasError') as ErrorStatus,
        errors: [initialPageData?.error] || [],
      };
    } else if (initialPageData.category?.category) {
      errorPayload = {
        pageType: 'category',
        categoryId: initialPageData.category.category.id,
        path,
        errorStatus: (initialPageData.error.extensions?.code ||
          'HasError') as ErrorStatus,
        errors: [initialPageData?.error] || [],
      };
    } else if (initialPageData.home) {
      errorPayload = {
        pageType: 'home',
        path,
        errorStatus: (initialPageData.error.extensions?.code ||
          'HasError') as ErrorStatus,
        errors: [initialPageData?.error] || [],
      };
    } else {
      errorPayload = {
        pageType: 'error',
        path,
        errorStatus: (initialPageData.error.extensions?.code ||
          'HasError') as ErrorStatus,
        errors: [initialPageData?.error] || [],
      };
    }

    const action = PageActions.pageNavError(errorPayload);
    return {
      page: pageNavError(pageInitialState, action),
    };
  }

  let payload:
    | ReturnType<typeof PageActions.pageNavComplete>['payload']
    | undefined;

  if (initialPageData.article?.article) {
    payload = {
      pageType: 'article',
      articleId: initialPageData.article.article.id,
      path,
      data: initialPageData.article,
    };
  } else if (initialPageData.category?.category) {
    payload = {
      pageType: 'category',
      categoryId: initialPageData.category.category.id,
      path,
      data: initialPageData.category,
    };
  } else if (initialPageData.home) {
    payload = {
      pageType: 'home',
      path,
      data: initialPageData.home,
    };
  }

  if (payload) {
    const action = PageActions.pageNavComplete(payload);
    return {
      page: pageNavComplete(pageInitialState, action),
    };
  }

  return {};
}

export function setupStore(initialPageData?: InitialData, path?: string) {
  const initialState = setupInitialState(initialPageData, path);

  const _store = createStore(
    reducer,
    initialState,
    composeWithDevTools({ trace: process.env.NODE_ENV !== 'production' })(
      applyMiddleware(sagaMiddleware, breadcrumbLogger)
    )
  );

  // fire up redux saga
  sagaMiddleware.run(rootSaga);
  return _store;
}

export type IStore = ReturnType<typeof setupStore>;
export type Dispatch = BaseDispatch<Actions>;
